import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Button,
  Text,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import { Play20Regular, Eye20Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchLabelDeploymentCoverage,
  clearLabels,
  selectDeployableLabels,
  selectLabelsLoading,
  selectLabelsError,
  selectLabelsPagination,
  setDeploymentInFlight,
  clearOverridesForEntities,
} from '../slices/labelDeploymentSlice';
import { selectAllTenants } from '../slices/tenantsSlice';
import { batchDeploy } from '../services/deployment.service';
import { trackDeployments, selectActiveDeploymentCount } from '../slices/deploymentTrackingSlice';
import type { LabelEntity } from '../services/types';
import type { BffDeploymentRequest, TrackedDeployment } from '../services/types';
import type { LabelUIStatus } from '../slices/labelDeploymentSlice';

import { StatusBadge, StatusType } from './StatusBadge';
import ExpandToggle from './common/ExpandToggle';
import CoverageCell from './common/CoverageCell';
import DeployDialog from './common/DeployDialog';
import DeploymentWarningBanner from './common/DeploymentWarningBanner';
import { FormattedDateTime, TimeColumnHeader } from './common/DateTimeWithUTCInfo';
import { ensureDomainSuffix } from './common/utils';
import {
  PolicyTable,
  EntityFilterBar,
  PoliciesPaginationFooter,
  DASHBOARD_GRID_COLUMNS_NO_PRIORITY,
} from './policies/shared';
import { DashboardLabelsTableStyles } from './Styles';
import LabelViewDetailsDialog from './labels/LabelViewDetailsDialog';
import LabelDashboardDeployConfirmDialog from './labels/LabelDashboardDeployConfirmDialog';
import type { Tenant } from './common/types';

// Grid for dashboard: expand(48px) | name(2.5fr) | coverage(1.5fr) | lastModified(1.5fr) | actions(1.5fr)
const LABEL_DASHBOARD_COLUMNS = DASHBOARD_GRID_COLUMNS_NO_PRIORITY;

function labelUIStatusToBadge(uiStatus: LabelUIStatus): StatusType {
  if (uiStatus === 'Deployed') return 'Available';
  if (uiStatus === 'InProgress') return 'In Progress';
  if (uiStatus === 'DeploymentFailed') return 'Failed';
  return 'Not Available';
}

interface LabelCoveragePopoverProps {
  deployedCount: number;
  totalCount: number;
  tenantDeployments: { tenantId: string; deploymentStatus: 'Deployed' | 'NotDeployed'; lastDeployedDateTime: string | null }[];
  getTenantName: (tenantId: string) => string;
  getEffectiveStatus?: (tenantId: string) => LabelUIStatus;
}

function LabelCoveragePopover({ deployedCount, totalCount, tenantDeployments, getTenantName, getEffectiveStatus }: LabelCoveragePopoverProps) {
  const coveragePct = totalCount > 0 ? Math.round((deployedCount / totalCount) * 100) : 0;
  const deployedTenants = tenantDeployments.filter(td => (getEffectiveStatus?.(td.tenantId) ?? td.deploymentStatus) === 'Deployed');
  const inProgressTenants = tenantDeployments.filter(td => (getEffectiveStatus?.(td.tenantId) ?? td.deploymentStatus) === 'InProgress');
  const notDeployedTenants = tenantDeployments.filter(td => {
    const s = getEffectiveStatus?.(td.tenantId) ?? td.deploymentStatus;
    return s !== 'Deployed' && s !== 'InProgress';
  });
  return (
    <Popover withArrow>
      <PopoverTrigger disableButtonEnhancement>
        <div style={{ cursor: 'pointer' }}>
          <CoverageCell countLabel={`${deployedCount}/${totalCount}`} percent={coveragePct} warnNoun="label" />
        </div>
      </PopoverTrigger>
      <PopoverSurface style={{ padding: 16, maxWidth: 300 }}>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontWeight: 600, fontSize: 14, display: 'block', marginBottom: 4 }}>Tenant Coverage</Text>
          <Text style={{ fontSize: 12, color: '#6b7280' }}>{deployedCount} of {totalCount} tenants have this label deployed</Text>
        </div>
        {deployedTenants.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <Text style={{ fontWeight: 600, fontSize: 12, color: '#107c10', display: 'block', marginBottom: 4 }}>
              ✓ Deployed ({deployedTenants.length})
            </Text>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#444' }}>
              {deployedTenants.map(td => (
                <li key={td.tenantId} style={{ marginBottom: 2 }}>{getTenantName(td.tenantId)}</li>
              ))}
            </ul>
          </div>
        )}
        {inProgressTenants.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <Text style={{ fontWeight: 600, fontSize: 12, color: '#0078d4', display: 'block', marginBottom: 4 }}>
              ⟳ In Progress ({inProgressTenants.length})
            </Text>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#444' }}>
              {inProgressTenants.map(td => (
                <li key={td.tenantId} style={{ marginBottom: 2 }}>{getTenantName(td.tenantId)}</li>
              ))}
            </ul>
          </div>
        )}
        {notDeployedTenants.length > 0 && (
          <div>
            <Text style={{ fontWeight: 600, fontSize: 12, color: '#d13438', display: 'block', marginBottom: 4 }}>
              ✗ Not Deployed ({notDeployedTenants.length})
            </Text>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#444' }}>
              {notDeployedTenants.map(td => (
                <li key={td.tenantId} style={{ marginBottom: 2 }}>{getTenantName(td.tenantId)}</li>
              ))}
            </ul>
          </div>
        )}
      </PopoverSurface>
    </Popover>
  );
}

type ScopeProps = {
  allTenantNames: string[];
  selectedTenants: string[];
};

export default function DashboardLabelsTable(_props: ScopeProps) {
  const styles = DashboardLabelsTableStyles();
  const dispatch = useAppDispatch();

  const labels = useAppSelector(selectDeployableLabels);
  const loading = useAppSelector(selectLabelsLoading);
  const error = useAppSelector(selectLabelsError);
  const pagination = useAppSelector(selectLabelsPagination);
  const allTenants = useAppSelector(selectAllTenants);
  const activeDeployCount = useAppSelector(selectActiveDeploymentCount);
  const uiStatusOverrides = useAppSelector(s => s.labelDeployment.uiStatusOverrides);

  const tenantNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of allTenants) m.set(t.id.toLowerCase(), ensureDomainSuffix(t.name));
    return m;
  }, [allTenants]);

  const tenantPrefixById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of allTenants) m.set(t.id.toLowerCase(), t.prefix ?? '');
    return m;
  }, [allTenants]);

  const getEffectiveTenantStatus = useCallback(
    (entityId: string, td: { tenantId: string; deploymentStatus: 'Deployed' | 'NotDeployed' }): LabelUIStatus => {
      const key = `${entityId.toLowerCase()}::${td.tenantId.toLowerCase()}`;
      const override = uiStatusOverrides[key] as LabelUIStatus | undefined;
      if (override) return override;
      return td.deploymentStatus === 'Deployed' ? 'Deployed' : 'NotDeployed';
    },
    [uiStatusOverrides],
  );

  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detailsLabel, setDetailsLabel] = useState<LabelEntity | null>(null);
  const [deployTarget, setDeployTarget] = useState<LabelEntity | null>(null);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [pendingTenantIds, setPendingTenantIds] = useState<string[]>([]);
  const [deployConfirmOpen, setDeployConfirmOpen] = useState(false);
  const [deploying, setDeploying] = useState<string | null>(null);
  const [deployMessage, setDeployMessage] = useState<{
    intent: 'success' | 'error' | 'warning' | 'info';
    title: string;
    body: string;
  } | null>(null);

  useEffect(() => {
    setPageNumber(1);
    dispatch(clearLabels());
  }, [dispatch]);

  // Server-side label search is not implemented in the BFF, so when a search term is
  // active we fetch a single large page and filter client-side, surfacing matches from
  // across all pages together (not just the current page). The 5000 cap is a safety
  // ceiling far above the realistic sensitivity-label count per tenant; matches beyond
  // it would not be fetched, so revisit if a tenant ever approaches that many labels.
  const isSearching = search.trim().length > 0;
  useEffect(() => {
    dispatch(
      fetchLabelDeploymentCoverage({
        searchTerm: search || undefined,
        pageNumber: isSearching ? 1 : pageNumber,
        pageSize: isSearching ? 5000 : 25,
      }),
    );
  }, [search, pageNumber, dispatch, isSearching]);

  const filtered = useMemo(() => {
    if (!search) return labels;
    const q = search.toLowerCase();
    return labels.filter(l => l.data.displayName.toLowerCase().includes(q));
  }, [labels, search]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Build Tenant[] for DeployDialog from a label's tenantDeployments
  const deployTenants: Tenant[] = useMemo(() => {
    if (!deployTarget) return [];
    return deployTarget.tenantDeployments.map(td => {
      const name = tenantNameById.get(td.tenantId.toLowerCase()) ?? td.tenantId;
      return {
        id: td.tenantId,
        name,
        domain: name,
        prefix: tenantPrefixById.get(td.tenantId.toLowerCase()) || undefined,
        status: td.deploymentStatus === 'Deployed' ? 'Available' : 'Not Available',
        lastScanned: td.lastDeployedDateTime ?? '',
        lastDeployed: td.lastDeployedDateTime ?? '',
        deployer: undefined,
      };
    });
  }, [deployTarget, tenantNameById, tenantPrefixById]);

  const handleDeployConfirm = useCallback(
    async (tenantIds: string[]) => {
      if (!deployTarget || tenantIds.length === 0) return;
      const label = deployTarget;
      const entityName = label.data.name;
      setDeploying(label.entityId);
      setDeployTarget(null);
      // Optimistic update: show InProgress immediately (same pattern as policies)
      dispatch(setDeploymentInFlight({ entityId: label.entityId, tenantIds }));
      try {
        const req: BffDeploymentRequest = {
          entityType: 'LABEL',
          entities: [
            {
              id: entityName,
              targets: tenantIds.map(tenantId => ({ tenantId })),
            },
          ],
        };
        const result = await batchDeploy(req);

        const now = Date.now();
        // BFF returns entityId = label name; map back to local GUID for
        // consistent polling keys.
        const tracked: TrackedDeployment[] = result.acceptedItems.map(a => ({
          deploymentId: a.deploymentId,
          tenantId: a.tenantId,
          entityId: label.entityId,
          policyName: entityName,
          entityType: 'LABEL',
          view: 'dashboard',
          apiStatus: 'Pending',
          startedAt: now,
          lastPolledAt: now,
        }));
        if (tracked.length > 0) dispatch(trackDeployments(tracked));

        if (result.success && result.failedCount === 0 && result.conflictCount === 0) {
          setDeployMessage({
            intent: 'success',
            title: 'Deployment requested',
            body: `${entityName} is being deployed to ${tenantIds.length} tenant(s). Parent and standalone labels are prefixed with each tenant's configured prefix; the prefixed name may take 5–10 minutes to sync, after which a page refresh will show it.`,
          });
        } else if (!result.success) {
          setDeployMessage({ intent: 'error', title: 'Deployment rejected', body: result.message });
        } else {
          setDeployMessage({ intent: 'warning', title: 'Partial deployment', body: result.message });
        }
      } catch (e) {
        // Roll back the optimistic InProgress overrides
        dispatch(clearOverridesForEntities(
          [{ entityId: label.entityId, tenantIds }],
        ));
        setDeployMessage({
          intent: 'error',
          title: 'Deployment failed',
          body: e instanceof Error ? e.message : 'Unknown error',
        });
      } finally {
        setDeploying(null);
      }
    },
    [deployTarget, dispatch],
  );

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        padding: '0 24px 24px 24px',
      }}
    >
      {/* Header — mirrors PolicyTableContainer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 0 20px 0',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Sensitivity labels</h2>
          <Text size={300} style={{ color: '#6b7280' }}>
            Manage sensitivity labels across all tenants
          </Text>
        </div>
        <div />
      </div>

      <div style={{ height: 1, background: '#e5e5e5', margin: '0 -24px 24px -24px' }} />

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Error loading labels</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Deployment result feedback */}
      {deployMessage && (
        <MessageBar intent={deployMessage.intent} style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>{deployMessage.title}</MessageBarTitle>
            {deployMessage.body}
          </MessageBarBody>
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss24Regular />}
            onClick={() => setDeployMessage(null)}
            aria-label="dismiss message"
          />
        </MessageBar>
      )}

      {/* Prefix redeploy warnings — non-blocking, dismissible (shared across views) */}
      <DeploymentWarningBanner />

      {/* Active deployment polling indicator */}
      {activeDeployCount > 0 && (
        <MessageBar intent="info" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Deployment In Progress</MessageBarTitle>
            {activeDeployCount} deployment{activeDeployCount > 1 ? 's' : ''} in progress. Status updates automatically every 15 seconds.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Filter bar — search only */}
      <EntityFilterBar
        search={search}
        onSearchChange={(v) => { setSearch(v); setPageNumber(1); }}
        showFilter={false}
        searchAriaLabel="Search labels"
        searchPlaceholder="Search labels..."
        className={styles.solidField}
      />

      {/* Table */}
      <PolicyTable
        columns={[
          { key: 'expand', label: '', width: '48px' },
          { key: 'name', label: 'Label Name', width: 'minmax(250px,2.5fr)' },
          { key: 'coverage', label: 'Tenant Coverage', width: 'minmax(180px,1.5fr)' },
          { key: 'lastModified', label: 'Last Modified', width: 'minmax(180px,1.5fr)', isTimeColumn: true },
          { key: 'actions', label: 'Actions', width: 'minmax(180px,1.5fr)', align: 'center' },
        ]}
        gridTemplateColumns={LABEL_DASHBOARD_COLUMNS}
        loading={loading}
        loadingMessage="Loading labels..."
        isEmpty={filtered.length === 0}
        emptyTitle="No labels found"
        emptyMessage="No sensitivity labels match your search"
      >
        {filtered.map((label, i) => {
          const isOpen = expanded.has(label.entityId);
          const isLastRow = i === filtered.length - 1;
          const lastMod = label.data.metadata.lastModifiedDateTime;
          const total = label.tenantDeployments.length;
          const deployedCount = label.deployedTenantsCount;

          return (
            <React.Fragment key={label.entityId}>
              {/* Main row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: LABEL_DASHBOARD_COLUMNS,
                  alignItems: 'center',
                  padding: '16px 20px',
                  fontSize: 14,
                  borderBottom: isLastRow && !isOpen ? 'none' : '1px solid #f0f0f0',
                  background: 'white',
                }}
              >
                <div>
                  <ExpandToggle
                    isOpen={isOpen}
                    onClick={() => toggleExpand(label.entityId)}
                    ariaLabel={isOpen ? 'Collapse row' : 'Expand row'}
                  />
                </div>
                <div style={{ fontWeight: 600 }}>{label.data.displayName}</div>
                <div>
                  <LabelCoveragePopover
                    deployedCount={deployedCount}
                    totalCount={total}
                    tenantDeployments={label.tenantDeployments}
                    getTenantName={(id) => tenantNameById.get(id.toLowerCase()) ?? id}
                    getEffectiveStatus={(tid) => getEffectiveTenantStatus(label.entityId, { tenantId: tid, deploymentStatus: label.tenantDeployments.find(d => d.tenantId === tid)?.deploymentStatus ?? 'NotDeployed' })}
                  />
                </div>
                <div>
                  {lastMod ? (
                    <FormattedDateTime dateTime={lastMod} />
                  ) : (
                    <span style={{ color: '#6b7280' }}>—</span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                  <Button
                    appearance="primary"
                    size="small"
                    icon={<Play20Regular />}
                    disabled={deploying === label.entityId}
                  onClick={() => { setDeployTarget(label); setDeployDialogOpen(true); }}
                    title="Deploy label to tenants"
                  >
                    Deploy
                  </Button>
                  <Button
                    appearance="secondary"
                    size="small"
                    icon={<Eye20Regular />}
                    onClick={() => setDetailsLabel(label)}
                  >
                    View Details
                  </Button>
                </div>
              </div>

              {/* Expanded per-tenant details row */}
              {isOpen && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: LABEL_DASHBOARD_COLUMNS,
                    padding: '12px 20px 16px 20px',
                    borderBottom: isLastRow ? 'none' : '1px solid #f0f0f0',
                    background: '#f8f9fa',
                  }}
                >
                  <div />
                  <div style={{ gridColumn: '2 / -1' }}>
                    <div
                      style={{
                        border: '1px solid #e5e5e5',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Expanded header */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(180px,1.5fr) minmax(100px,0.8fr) minmax(120px,1fr) minmax(180px,1.5fr)',
                          padding: '12px 16px',
                          background: '#fafafa',
                          borderBottom: '1px solid #e5e5e5',
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12 }}>Tenant Name</div>
                        <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12, textAlign: 'center' }}>Prefix</div>
                        <div style={{ borderRight: '1px solid #e5e5e5', paddingRight: 12, textAlign: 'center' }}>Status</div>
                        <div style={{ paddingLeft: 12, display: 'flex', justifyContent: 'center' }}>
                          <TimeColumnHeader>Last Modified</TimeColumnHeader>
                        </div>
                      </div>
                      {/* Expanded rows */}
                      {label.tenantDeployments.map((td, tdIdx) => (
                        <div
                          key={td.tenantId}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(180px,1.5fr) minmax(100px,0.8fr) minmax(120px,1fr) minmax(180px,1.5fr)',
                            padding: '10px 16px',
                            borderBottom: tdIdx === label.tenantDeployments.length - 1 ? 'none' : '1px solid #f0f0f0',
                            background: 'white',
                            fontSize: 13,
                          }}
                        >
                          <div style={{ borderRight: '1px solid #f0f0f0', paddingRight: 12 }}>
                            {tenantNameById.get(td.tenantId.toLowerCase()) ?? td.tenantId}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center', borderRight: '1px solid #f0f0f0', paddingRight: 12 }}>
                            {tenantPrefixById.get(td.tenantId.toLowerCase()) ? (
                              <span style={{ fontWeight: 600, color: '#0078d4' }}>
                                {tenantPrefixById.get(td.tenantId.toLowerCase())}
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280' }}>—</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center', borderRight: '1px solid #f0f0f0', paddingRight: 12 }}>
                            <StatusBadge status={labelUIStatusToBadge(getEffectiveTenantStatus(label.entityId, td))} />
                          </div>
                          <div style={{ paddingLeft: 12, display: 'flex', justifyContent: 'center' }}>
                            {label.data.metadata.lastModifiedDateTime ? (
                              <FormattedDateTime dateTime={label.data.metadata.lastModifiedDateTime} />
                            ) : (
                              <span style={{ color: '#6b7280' }}>—</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </PolicyTable>

      {/* Pagination — hidden while searching (all matches shown on one page) */}
      {!isSearching && (
        <PoliciesPaginationFooter
          pageNumber={pagination.pageNumber}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          hasNextPage={pagination.hasNextPage}
          loading={loading}
          onPageChange={setPageNumber}
        />
      )}

      {/* View Details Dialog */}
      <LabelViewDetailsDialog
        open={!!detailsLabel}
        onClose={() => setDetailsLabel(null)}
        label={detailsLabel}
      />

      {/* Deploy Dialog — tenant selection */}
      <DeployDialog
        open={deployDialogOpen}
        onClose={() => { setDeployDialogOpen(false); setDeployTarget(null); }}
        onConfirm={(ids) => { setDeployDialogOpen(false); setPendingTenantIds(ids); setDeployConfirmOpen(true); }}
        subjectName={deployTarget?.data.displayName ?? ''}
        subjectKind="label"
        tenants={deployTenants}
      />

      {/* Deploy Confirmation Dialog */}
      <LabelDashboardDeployConfirmDialog
        open={deployConfirmOpen}
        label={deployTarget}
        tenantIds={pendingTenantIds}
        tenantNamesById={tenantNameById}
        tenantPrefixById={tenantPrefixById}
        baseTenantName={deployTarget ? undefined : undefined}
        deploying={deploying === deployTarget?.entityId}
        onConfirm={() => { setDeployConfirmOpen(false); handleDeployConfirm(pendingTenantIds); }}
        onClose={() => { setDeployConfirmOpen(false); setDeployTarget(null); setPendingTenantIds([]); }}
      />
    </div>
  );
}
